import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Crown,
  Calendar,
  Lock,
  Trash2,
  Shield,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ChevronLeft,
  Mail,
} from 'lucide-react';
import { EmailPreferences, ActivityTracking, InternalMessagesPref } from '@/components/EmailPreferences';

const MONTHS_PT = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

function formatDatePT(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${d.getDate()} de ${MONTHS_PT[d.getMonth()]} de ${d.getFullYear()}`;
  } catch {
    return iso;
  }
}

function SubscriptionCard({ subscription }) {
  if (!subscription) return null;

  // Admin bypass — has Premium-like access but no expiry
  if (subscription.is_admin_bypass) {
    return (
      <div className="bg-surface rounded-2xl border border-gold/40 p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold to-amber-600 flex items-center justify-center shrink-0">
            <Shield className="w-6 h-6 text-background" />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider text-gold mb-1">Conta de Administrador</p>
            <h2 className="text-xl font-bold text-foreground mb-2">Acesso ilimitado</h2>
            <p className="text-sm text-muted-foreground">
              Tens acesso completo a toda a plataforma como administrador. Não tens data de expiração.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Active Premium subscriber
  if (subscription.is_active) {
    const daysLeft = subscription.days_remaining ?? 0;
    const planLabel = subscription.label || 'Premium';

    // Visual urgency
    let badgeColor = 'text-gold border-gold/40';
    let progressColor = 'bg-gradient-to-r from-ruby to-gold';
    if (daysLeft <= 7) {
      badgeColor = 'text-red-400 border-red-500/40';
      progressColor = 'bg-red-500';
    } else if (daysLeft <= 30) {
      badgeColor = 'text-amber-400 border-amber-500/40';
      progressColor = 'bg-amber-500';
    }

    return (
      <div className={`bg-surface rounded-2xl border-2 ${badgeColor} p-6 md:p-8`}>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ruby to-gold flex items-center justify-center shrink-0">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider text-gold mb-1">Subscrição ativa</p>
            <h2 className="text-xl font-bold text-foreground mb-1">{planLabel}</h2>
            <p className="text-sm text-muted-foreground">
              Válida até <strong className="text-foreground">{formatDatePT(subscription.premium_until)}</strong>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-surface-light rounded-xl p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Dias restantes</p>
            <p className="text-3xl font-bold text-foreground" data-testid="subscription-days-remaining">{daysLeft}</p>
          </div>
          <div className="bg-surface-light rounded-xl p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Origem</p>
            <p className="text-base font-medium text-foreground capitalize">
              {subscription.source === 'woocommerce' ? 'Loja WooCommerce' : 'Ativação manual'}
            </p>
          </div>
        </div>

        {daysLeft <= 30 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-200 mb-1">A tua subscrição está a chegar ao fim</p>
              <p className="text-amber-200/80">
                Renova já para não perderes o acesso aos contos exclusivos.{' '}
                <a href="/premium" className="underline font-medium hover:text-amber-100">Ver planos</a>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Free user
  return (
    <div className="bg-surface rounded-2xl border border-border p-6 md:p-8">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-xl bg-surface-light flex items-center justify-center shrink-0">
          <Crown className="w-6 h-6 text-muted-foreground" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Plano atual</p>
          <h2 className="text-xl font-bold text-foreground mb-1">Gratuito</h2>
          <p className="text-sm text-muted-foreground">
            Desbloqueia todo o catálogo com Premium — séries, contos exclusivos e arquivo completo.
          </p>
        </div>
      </div>
      <Button
        onClick={() => (window.location.href = '/premium')}
        data-testid="subscription-upgrade-btn"
        className="w-full bg-gradient-to-r from-ruby to-gold text-white rounded-full py-6 font-semibold hover:opacity-90"
      >
        <Crown className="w-4 h-4 mr-2" />
        Subscrever Premium
      </Button>
    </div>
  );
}

function ChangePasswordSection() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error('A nova palavra-passe tem de ter pelo menos 8 caracteres.');
      return;
    }
    if (next !== confirm) {
      toast.error('As palavras-passe novas não coincidem.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      toast.success('Palavra-passe alterada com sucesso.');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao alterar a palavra-passe.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-surface rounded-2xl border border-border p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-surface-light flex items-center justify-center">
          <Lock className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-foreground">Alterar palavra-passe</h2>
          <p className="text-sm text-muted-foreground">Mínimo de 8 caracteres</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="current-pass" className="text-sm text-foreground mb-2 block">Palavra-passe atual</Label>
          <Input
            id="current-pass"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            data-testid="change-password-current"
            required
            autoComplete="current-password"
          />
        </div>
        <div>
          <Label htmlFor="new-pass" className="text-sm text-foreground mb-2 block">Nova palavra-passe</Label>
          <Input
            id="new-pass"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            data-testid="change-password-new"
            required
            autoComplete="new-password"
            minLength={8}
          />
        </div>
        <div>
          <Label htmlFor="confirm-pass" className="text-sm text-foreground mb-2 block">Confirmar nova palavra-passe</Label>
          <Input
            id="confirm-pass"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="change-password-confirm"
            required
            autoComplete="new-password"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="change-password-submit"
          className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</>
          ) : (
            <><CheckCircle2 className="w-4 h-4 mr-2" />Guardar nova palavra-passe</>
          )}
        </Button>
      </form>
    </div>
  );
}

function DeleteAccountSection({ onDeleted, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDelete = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.delete('/auth/me', {
        data: { password, confirmation },
      });
      toast.success('Conta eliminada. Adeus!');
      setOpen(false);
      onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao eliminar a conta.');
    } finally {
      setLoading(false);
    }
  };

  if (isAdmin) {
    return (
      <div className="bg-surface rounded-2xl border border-border p-6 md:p-8 opacity-60">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-gold shrink-0 mt-1" />
          <div>
            <h2 className="text-lg font-bold text-foreground mb-1">Eliminação de conta indisponível</h2>
            <p className="text-sm text-muted-foreground">
              Contas de administrador não podem ser eliminadas por esta zona. Contacta o suporte se for necessário.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-surface rounded-2xl border border-red-500/30 p-6 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">Eliminar conta</h2>
            <p className="text-sm text-muted-foreground">Esta ação é permanente e não pode ser desfeita.</p>
          </div>
        </div>

        <div className="rounded-xl bg-red-500/5 border border-red-500/20 p-4 mb-5">
          <p className="text-sm text-red-200/90 leading-relaxed">
            Ao eliminar a tua conta vais perder permanentemente:
          </p>
          <ul className="mt-2 text-sm text-red-200/80 list-disc list-inside space-y-1">
            <li>Acesso Premium e dias restantes</li>
            <li>Favoritos e histórico de leitura</li>
            <li>Pontos de fidelidade acumulados</li>
            <li>Comentários e confissões publicadas</li>
            <li>Qualquer outro conteúdo associado à conta</li>
          </ul>
          <p className="mt-3 text-xs text-red-300/70">
            Nota: o histórico de compras é mantido por motivos legais e financeiros.
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid="delete-account-open"
          className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-full py-6"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Eliminar a minha conta
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Confirmar eliminação
            </DialogTitle>
            <DialogDescription>
              Esta ação é permanente. Para confirmar, escreve <strong className="text-foreground">ELIMINAR</strong> em maiúsculas e insere a tua palavra-passe.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleDelete} className="space-y-4">
            <div>
              <Label htmlFor="delete-pass" className="text-sm text-foreground mb-2 block">Palavra-passe</Label>
              <Input
                id="delete-pass"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="delete-account-password"
                required
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label htmlFor="delete-confirm" className="text-sm text-foreground mb-2 block">
                Escreve <strong>ELIMINAR</strong> para confirmar
              </Label>
              <Input
                id="delete-confirm"
                type="text"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                data-testid="delete-account-confirmation"
                placeholder="ELIMINAR"
                required
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid="delete-account-cancel"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading || confirmation !== 'ELIMINAR' || !password}
                data-testid="delete-account-confirm"
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A eliminar...</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" />Eliminar definitivamente</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AccountSettings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/auth/me/subscription');
        if (!cancelled) setSubscription(data);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load subscription', err);
          toast.error('Não foi possível carregar a subscrição.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAccountDeleted = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-20 sm:pt-24 pb-24 sm:pb-12 px-4 sm:px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/profile')}
            data-testid="account-back-btn"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Voltar ao perfil
          </button>

          <header className="mb-8">
            <h1 className="font-brand text-3xl font-bold text-foreground mb-2">Definições da Conta</h1>
            <p className="text-muted-foreground">
              Gere a tua subscrição, palavra-passe e eliminação de conta.
            </p>
          </header>

          <div className="space-y-6">
            <section data-testid="subscription-section">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Subscrição</h3>
              </div>
              {loading ? (
                <div className="bg-surface rounded-2xl border border-border p-8 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-ruby" />
                </div>
              ) : (
                <SubscriptionCard subscription={subscription} />
              )}
            </section>

            <section data-testid="password-section">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Segurança</h3>
              </div>
              <ChangePasswordSection />
            </section>

            <section data-testid="email-section">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Comunicações</h3>
              </div>
              <EmailPreferences />
              <div className="mt-4"><InternalMessagesPref /></div>
            </section>

            <section data-testid="privacy-section">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Privacidade</h3>
              </div>
              <ActivityTracking />
            </section>

            <section data-testid="delete-section">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-red-400/70" />
                <h3 className="text-xs uppercase tracking-wider text-red-400/70 font-medium">Zona de perigo</h3>
              </div>
              <DeleteAccountSection
                onDeleted={handleAccountDeleted}
                isAdmin={!!user?.is_admin}
              />
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
